import React from 'react';
import { Select, Input, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import useProjectContext from '../../../../contexts/ProjectContext';
import useGlossarySelect from './Inputs/useGlossarySelect';

/**
 * Custom hook for managing actor fields in a form.
 *
 * @hook
 * @param {boolean} loading - Indicates whether data is loading.
 * @param {Object} defaultGroup - The default actor group.
 * @param {Array} actors - An array of existing actors.
 * @param {Object} selectedUserId - The ID of the selected user.
 * @returns {Object} An object containing actor field configuration for a form.
 */
const useActorFields = (loading, defaultGroup, actors, selectedUserId) => {
  const { t } = useTranslation();
  const { project } = useProjectContext();

  const roleSelect = useGlossarySelect(
    project?.groupRole_list,
    loading,
    'role'
  );

  const activeActors = defaultGroup?.actors?.filter(
    (actor) =>
      actor.user &&
      (!actors.find((a) => a.user?._id === actor.user._id) ||
        actor.user._id === selectedUserId)
  );

  const actorFields = [
    {
      name: ['actors_list', 'actor', '_id'],
      hidden: true,
      noStyle: true
    },
    {
      name: ['actors_list', 'actor', 'user'],
      rules: [{ required: true }],
      input: (
        <Select>
          {activeActors?.map((actor) => (
            <Select.Option key={actor._id} value={actor.user?._id}>
              {`${actor.first_name} ${actor.last_name}`}
            </Select.Option>
          ))}
        </Select>
      )
    },
    {
      name: ['actors_list', 'actor', 'code'],
      rules: [{ required: false }],
      input: <Input row={2} />
    },
    {
      name: ['actors_list', 'actor', 'role'],
      rules: [{ required: false }],
      input: roleSelect
    },
    {
      name: ['actors_list', 'actor', 'coefficient'],
      input: (
        <Input
          type="number"
          min={0}
          max={100}
          suffix={
            <Tooltip title={t('projects.form.tooltip.actor_utility')}>
              <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
            </Tooltip>
          }
        />
      )
    }
  ];

  return { actorFields };
};

export default useActorFields;
